import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { TwoFactorAuthStorage } from "../../lib/twoFactorAuth";

// Admin credentials - in a real app, these would be stored securely
const ADMIN_EMAIL = "admin@rawpa.com";
const ADMIN_PASSWORD = "RawpaAdmin2024!";

export const AdminLogin = (): JSX.Element => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const auth = getAuth();

  useEffect(() => {
    // Check if this is the first time login (no 2FA setup)
    const has2FA = TwoFactorAuthStorage.isSetup();
    if (has2FA) {
      setIsFirstTime(false);
    } else {
      setIsFirstTime(true);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Check if credentials match admin credentials
    if (formData.email !== ADMIN_EMAIL || formData.password !== ADMIN_PASSWORD) {
      setError("Invalid admin credentials");
      setLoading(false);
      return;
    }

    try {
      // Try to sign in first
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          ADMIN_EMAIL,
          ADMIN_PASSWORD
        );
        if (userCredential.user.email === ADMIN_EMAIL) {
          // Check if 2FA is set up
          if (TwoFactorAuthStorage.isSetup()) {
            // Redirect to 2FA verification
            navigate('/aAyuiOcvMq/2fa-verify');
          } else {
            // First time login - redirect to 2FA setup
            navigate('/aAyuiOcvMq/2fa-setup');
          }
          return;
        }
      } catch (signInError) {
        // If sign in fails, try to create the account
        try {
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            ADMIN_EMAIL,
            ADMIN_PASSWORD
          );
          if (userCredential.user.email === ADMIN_EMAIL) {
            // First time login - redirect to 2FA setup
            navigate('/aAyuiOcvMq/2fa-setup');
            return;
          }
        } catch (createError: any) {
          if (createError.code === 'auth/email-already-in-use') {
            setError("Admin account exists but credentials are incorrect");
          } else {
            setError("Failed to create admin account: " + createError.message);
          }
        }
      }
    } catch (err: any) {
      setError("Authentication error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-screen">
        <div className="w-full max-w-[480px] bg-white rounded-[20px] p-8 shadow-lg mt-[140px]">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-[#daed6e] rounded-full flex items-center justify-center mb-4">
              <img src="/security-icon.svg" alt="Admin" className="w-8 h-8" />
            </div>
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Admin Access
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 text-center">
              {isFirstTime ? 'Set up secure admin access' : 'Secure admin panel access'}
            </p>
            {isFirstTime && (
              <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700 text-center">
                  First time login: You'll be prompted to set up two-factor authentication
                </p>
              </div>
            )}
          </div>
          
          <form className="space-y-4" onSubmit={handleLogin}>
            {error && (
              <div className="p-3 text-sm text-red-500 bg-red-50 rounded-lg">
                {error}
              </div>
            )}
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className="h-12 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-base focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder="Admin Email"
              required
            />
            <Input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              className="h-12 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-base focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
              placeholder="Admin Password"
              required
            />

            <Button 
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                {loading ? 'Authenticating...' : (isFirstTime ? 'Setup Admin Access' : 'Access Admin Panel')}
              </span>
            </Button>

            <div className="mt-4 text-center">
              <p className="text-sm text-black opacity-80">
                Admin Credentials for Testing:<br />
                Email: {ADMIN_EMAIL}<br />
                Password: {ADMIN_PASSWORD}
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 